<!-- Component for selecting a package to view its details -->
<template>
	<div class="flex w-full flex-col gap-10 md:flex-row">
		<!--Package Image-->
		<div class="items-left flex w-full flex-col justify-start md:w-1/2">
			<div
				class="relative aspect-435/220 w-full overflow-hidden rounded-lg shadow-lg md:aspect-435/360"
			>
				<div
					class="absolute inset-0 bg-cover bg-center bg-no-repeat"
					:style="`background-image: url(${imageUrl});`"
				></div>
			</div>

			<div
				v-if="recommendation"
				class="mt-3"
			>
				<div class="text-xs font-normal text-black italic">
					{{
						t('packageSlide.recommendedFor', {
							name: recommendationNames,
						})
					}}
				</div>
				<div class="mt-2 flex flex-row justify-start gap-2">
					<div
						class="rounded-[20px] px-4 py-2 text-xs font-bold text-black"
						:class="reasonColors[recPlanIndex]"
						v-for="(reason, index) in recommendation.reasons"
						:key="reason"
					>
						{{ t(`packageSlide.reason.${reason}`) }}
					</div>
				</div>
			</div>
		</div>
		<!--Package Details Section-->
		<div class="w-full overflow-hidden md:w-1/2">
			<div
				class="ml-0.25 flex gap-2 pb-3"
				v-if="
					sessionStore.activePlans?.some((plan) => isLineItemInPlan(pkg, plan))
				"
			>
				<PlanAvatar
					v-for="(plan, index) in sessionStore.activePlans"
					v-show="isLineItemInPlan(pkg, plan)"
					:key="plan.id"
					:index="index"
					class="mt-2 cursor-default!"
					:filled="true"
					:plan="plan"
				/>
			</div>

			<div class="flex w-full flex-col gap-7">
				<div>
					<div class="flex items-start gap-2">
						<h2 class="text-secondary-900 text-2xl font-bold">
							{{ pkg.name }}
						</h2>
						<ShieldCheckIcon class="text-accent-600 mt-1 size-6 min-w-6" />
					</div>

					<p class="mt-2 text-base text-slate-500">
						{{ pkg.description }}
					</p>
				</div>

				<button
					type="button"
					class="text-md bg-accent-400 text-accent-700 hover:bg-accent-500 w-full max-w-33.25 rounded-lg px-6 py-4 font-semibold hover:text-white"
					@click="$emit('select', pkg)"
				>
					{{ t('general.select') }}
				</button>

				<div class="text-primary-700 w-full text-4xl font-bold">
					{{ formatPrice(packageTotal + (mockProductOap?.fixedPrice ?? 0)) }}
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { GroupedRecommendation } from '../slides/PackagesSlide.vue'
import type { PbLineItemPackage } from '@/apiTypes'
import PlanAvatar from '@/components/presentation/PlanAvatar.vue'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type { AssetStoryblok } from '@/sbTypes'
import { useAssetStore } from '@/stores/asset'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import ShieldCheckIcon from '@heroicons/vue/24/solid/ShieldCheckIcon'
import { computedAsync } from '@vueuse/core'
import { computed, type PropType, ref, watch } from 'vue'

const presentation = usePresentationController()

const showOverlay = ref(false)

watch(
	() => presentation.activeSlideId,
	() => {
		showOverlay.value = false
	}
)

const { t } = usePresentationLocale()

const props = defineProps({
	pkg: {
		type: Object as PropType<PbLineItemPackage>,
		required: true,
	},
	recommendation: {
		type: Object as PropType<GroupedRecommendation>,
		default: undefined,
	},
})

const { formatPrice, isLineItemInPlan, calculatePackageTotal, mockProductOap } =
	useMerchandise()
const sessionStore = useSessionStore()
const asset = useAssetStore()

defineEmits<{
	(event: 'select', pkg: PbLineItemPackage): void
}>()

/** The image blob url */
const imageUrl = computedAsync(() =>
	asset.getImageBlobUrl({
		filename: props.pkg.productImageUrl,
		name: props.pkg.name,
	} as AssetStoryblok)
)

const reasonColors = [
	'bg-sky-200',
	'bg-indigo-200',
	'bg-violet-200',
	'bg-fuchsia-200',
	'bg-teal-200',
	'bg-lime-200',
	'bg-amber-200',
	'bg-rose-200',
	'bg-cyan-200',
]

/** String including names of all plans for this recommendation */
const recommendationNames = computed(() => {
	if (!props.recommendation) return ''

	return props.recommendation.plans.map((plan) => plan.firstName).join(' & ')
})

/** The index of the first plan for the recommendation */
const recPlanIndex = computed(() => {
	if (props.recommendation == undefined) return 0

	const planId = props.recommendation.plans[0].id
	return sessionStore.activePlans.findIndex((plan) => plan.id === planId)
})

const packageTotal = computed(() => calculatePackageTotal(props.pkg.id))
</script>
